import React from 'react';

import { Menu } from '@lumapps/lumx-menu/components/Menu';
import { ButtonProps, DropdownProps } from '@lumapps/lumx/react';

interface CustomizationDropdownProps extends DropdownProps {
    children: React.ReactNode;
    buttonProps?: ButtonProps;
    label: string;
}

/**
 * Wrapper for the DS Dropdown in order to allow the customizations API to use
 * dropdowns as easily as possible. This specifically avoids developers from the customizations API
 * to need the `useRef` hook outside of React.
 * @param CustomizationDropdownProps
 */
const FOCUSABLE_SELECTOR =
    '[role^=menuitem], input, textarea, select, button, [contenteditable], [tabindex]:not([tabindex="-1"])';

export const CustomizationDropdown: React.FC<CustomizationDropdownProps> = ({
    children,
    buttonProps = {},
    label,
    ...props
}) => {
    const handleMouseDown = React.useCallback((event: React.MouseEvent) => {
        // Prevent focus from leaving the trigger when clicking non-interactive content.
        // Without this, clicking a non-focusable element (e.g. a Card) inside the menu
        // moves focus to document.body and triggers the blur-based close.
        if (!(event.target as HTMLElement).closest(FOCUSABLE_SELECTOR)) {
            event.preventDefault();
        }
    }, []);

    return (
        <Menu.Trigger popoverProps={props}>
            <Menu.TriggerButton {...buttonProps}>{label}</Menu.TriggerButton>
            <Menu onMouseDown={handleMouseDown}>{children}</Menu>
        </Menu.Trigger>
    );
};

CustomizationDropdown.displayName = 'Dropdown';
